import React, { useMemo } from 'react';
import { useQuery } from "@tanstack/react-query";
import { getEventRanking } from "data/Querys";
import { ColumnRankNoResult, ColumnName, ColumnNoc, ColumnExpander, ColumnIrmRanking, ColumnTeamAthletes } from "components/common/TableColumns"
import Label from 'components/general/Label';
import Loader from 'components/general/Loader';
import MirsTable from 'components/general/table/Table.js';
import NotAvailable from 'components/common/NotAvailable';
import LoaderSubComponent from 'components/general/LoaderSubComponent';
import { NocMemo as Noc } from 'components/presentation/Noc';
import Competitor from 'components/presentation/Competitor';
import SportCode from "components/common/SportCode";
import { findArray, isParalympic, CompetitionFromStore } from 'helpers/utils';
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import { usePositions, useTeamAthletesOrderByPositions } from "components/common/Positions";
import Stack from 'react-bootstrap/Stack';
import { getRscObject } from 'helpers/rsc';
import useViewport from 'components/general/useViewPort';
import { LegendProvider } from "components/common/legendContext";
import { CleanLegends } from 'components/common/CleanLegends';
import { LegendsBox } from 'components/common/LegendsBox';
import SvgMirs from 'components/presentation/SvgMirs';

function EventRanking() {
  const { mirs_event, mirs_competition, mirs_language } = useUrlFriendlyToParameters()
  const filterScheduled = getScheduledFilter(mirs_event)
  const { data, isError, isLoading } = useQuery(
    {
      queryKey: ['eventRanking', mirs_competition, mirs_event, mirs_language, filterScheduled],
      queryFn: getEventRanking,
      refetchInterval: 20000,
      staleTime: 20000,
      refetchIntervalInBackground: true,
    });

  if (isError) { return <NotAvailable label={"LabelNoDataRanking"} />; }
  if (isLoading) { return <Loader />; }
  if (!data?.event?.rankings) { return <NotAvailable label={"LabelNoDataRanking"} /> }


  return (
    <EventRanking2 data={data} />
  )


}

export function getScheduledFilter(rsc) {
  return /EQU.DRES.+/.test(rsc) ? 'A' : 'Y'
}



function EventRanking2({ data }) {
  const competition = CompetitionFromStore();
  const isPara = isParalympic(competition);
  const unitDescriptionForFinal = ['FEN', 'CLB', 'SHO']
  const unitDescription = isPara ? ['CLB', 'TKW', 'BKB', 'RU7', 'HOC', 'BKG', 'SHO', 'SRF', 'BK3', 'VVO']
    : ['CLB', 'EQU', 'TKW', 'BKB', 'RU7', 'HOC', 'BKG', 'SHO', 'SRF', 'BK3', 'VVO'];
  const { mirs_discipline, mirs_event } = useUrlFriendlyToParameters()

  const isTeam = mirs_event?.includes("TEAM")

  const tableColumnsDetail = (disc, street, cumulativeData) => {
    let classNameConditions = disc === 'DIV' ? "text-start w-5" : "text-start w-30"
    return (
      [{
        id: 'Phase',
        Header: <Label data={'HeaderPhase'} />,
        headerClassName: classNameConditions,
        className: 'text-start',
        accessor: (row) => (row?.phaseOrder),
        Cell: (props) => {
          let row = props?.cell?.row?.original
          const arrDisciplineUnit = ['CSP', 'ROW', 'CTR', 'VBV', 'HBL']
          const arrDisciplineFinalsMedal = ['FBL']
          const specificSFNL = ['MPN']
          const SFNLType = (specificSFNL.includes(disc) && row?.phase === "Semi-finals")
            && row?.result?.props?.rankingResult?.extendedResults?.find(e => e.er_code === "SEMIFINAL")?.er_pos
          return (
            <>
              {arrDisciplineUnit.includes(disc)
                ? row?.unit
                : arrDisciplineFinalsMedal && row?.phase === 'Finals'
                  ? `${row?.phase + ' - ' + row?.unit}`
                  : <>{row?.phase} {SFNLType}</>}
            </>
          )
        }
      },
      {
        id: 'Noc',
        Header: <Label data={'HeaderVS'} />,
        headerClassName: "text-start",
        className: 'text-start',
        Cell: (props) => {
          let isSeeding = false
          if (props.cell.row.original?.eventUnitCode?.includes('SD') && mirs_discipline === 'CLB')
            isSeeding = true
          let rivalName = props.cell.row.original?.rival
            ? <Competitor hidePicture={true} {...props.cell.row.original?.rival} />
            : <SportCode discipline={mirs_discipline} entity='@CompetitorPlace' code={props?.cell?.row?.original?.competitorPlace} />
          return (
            <>
              {isSeeding
                ? <></>
                :
                <>
                  <div className='d-flex align-items-center'>
                    <div className='event-ranking-noc d-none d-md-block'>
                      <Noc {...props?.cell?.row?.original?.rivalNoc} />
                    </div>

                    {rivalName}
                  </div>
                </>
              }
            </>
          )
        }
      },
      {
        id: 'NocArc',
        Header: <Label data={'HeaderVS'} />,
        headerClassName: "text-start",
        className: 'text-start',
        Cell: (props) => {
          let rivalName;
          let rivalNoc;
          if (props?.cell?.row?.original?.eventUnitCode.substring(22, 26) !== 'QUAL') {
            rivalName = props.cell.row.original?.rival
              ? <Competitor hidePicture={true} {...props.cell.row.original?.rival} />
              : <SportCode discipline={mirs_discipline} entity='@CompetitorPlace' code={props?.cell?.row?.original?.competitorPlace} />
            rivalNoc = <Noc {...props?.cell?.row?.original?.rivalNoc} />
          }
          return (
            <>
              <div className='d-flex align-items-center'>
                <div className='event-ranking-noc d-none d-md-block'>
                  {rivalNoc}
                </div>
                {rivalName}
              </div>
            </>
          )
        }
      },
      {
        id: 'Pos',
        Header: <Label data={'HeaderPlace'} />,
        headerClassName: "text-start",
        className: 'text-start',
        Cell: (props) => {
          return (
            <>
              {props?.cell?.row?.original?.teamPos}
            </>
          )
        }
      },
      {
        id: 'Rank',
        Header: <Label data={'HeaderRank'} />,
        headerClassName: '|SKB|ROW|CSP|BMX|DIV'.includes(disc)
          ? 'text-start w-1'
          : 'text-end',
        className: '|SKB|ROW|CSP|BMX|DIV'.includes(disc)
          ? 'text-start w-1'
          : 'text-end',
        Cell: (props) => {
          return (
            <>
              {/EQU.EVE.+/.test(mirs_event) ? <RankCumulativeEqu row={props?.cell?.row?.original} cumulativeData={cumulativeData} /> : <>{props?.cell?.row?.original?.teamRank}</>}
            </>
          )
        }
      },
      {
        id: 'Run1',
        Header: <Label data={'HeaderRun1'} sport={'BMF'} />,
        headerClassName: "text-end",
        className: 'text-end',
        Cell: (props) => {
          return (
            <>
              {props?.cell?.row?.original?.run1}
            </>
          )
        }
      },
      {
        id: 'Run2',
        Header: <Label data={'HeaderRun2'} sport={'BMF'} />,
        headerClassName: "text-end",
        className: 'text-end',
        Cell: (props) => {
          return (
            <>
              {props?.cell?.row?.original?.run2}
            </>
          )
        }
      },
      {
        id: 'WLT',
        className: 'text-end w-5',
        Cell: (props) => {
          let wlt = props?.cell?.row?.original?.teamWLT?.trim() === 'W' ? <SvgMirs className='mirsIcon' title='HeaderWinner' code='check' /> : ''
          if (mirs_discipline === 'FEN') {
            wlt = props?.cell?.row?.original?.teamWLT?.trim() === 'W' || props?.cell?.row?.original?.teamWLT?.trim() === 'V(T)'
              ? <SvgMirs className='mirsIcon' title='HeaderWinner' code='check' />
              : ''
          }
          return (
            <>
              {wlt}
            </>
          )
        }
      },
      {
        id: 'WLTRank',
        className: 'text-end w-5',
        Cell: (props) => {
          const row = props?.cell?.row?.original;
          const isClbSeeding = /CLB...........................SD-+/.test(row?.eventUnitCode)
          const wlt = row?.teamWLT?.trim() === 'W'
            ? <SvgMirs className='mirsIcon' title='HeaderWinner' code='check' />
            : row?.teamRank;
          return (
            <>
              {isClbSeeding ? row?.teamRank : wlt}
            </>
          )
        }
      },
      {
        id: 'Points',
        Header: <Label data={'HeaderPoints'} sport={'BMX'} />,
        headerClassName: "text-start w-10",
        className: 'w-10',
        Cell: (props) => {
          return (
            <>
              {props?.cell?.row?.original?.points}
            </>
          )
        }
      },
      {
        id: 'Result',
        Header: '|DIV|MPN|'.includes(disc) || /CLB.BLCOMB-.+/.test(mirs_event)
          ? <Label data={'HeaderPoints'} />
          : '|CSL|CLB|ROW|CSP|SWM|BMX'.includes(disc)
            ? <Label data={'HeaderTime'} />
            : disc === 'BMF'
              ? <Label data={'HeaderBestAvg'} sport={'BMF'} />
              : disc === 'SKB'
                ? <Label data={street ? 'HeaderTotal' : 'HeaderScore'} />
                : '|GRY|KTE|GTR'.includes(disc)
                  ? <Label data={'HeaderScore'} />
                  : /EQU.DRES.+/.test(mirs_event)
                    ? <Label data={'HeaderTotPercentScore'} />
                    : '|EQU|'.includes(disc)
                      ? <Label data={''} />
                      : '|SHO|SRF|'.includes(disc)
                        ? <Label data={'HeaderTotal'} />
                        : <Label data={'HeaderResult'} />,
        headerClassName: '|GRY|DIV'.includes(disc)
          ? 'text-center w-15'
          : '|CLB|FEN|BOX|EQU|SHO|HOC|MPN'.includes(disc)
            ? 'text-end w-30'
            : 'WRE'.includes(disc)
              ? 'text-start w-40'
              : '|HBL|TTE'.includes(disc)
                ? 'text-center w-30'
                : '|VVO|VBS'.includes(disc)
                  ? 'text-center'
                  : 'text-center w-15',
        className: '|BMF|ROW|CSP|BMX|SRF|DIV'.includes(disc)
          ? 'w-15'
          : '|SKB'.includes(disc)
            ? 'text-start'
            : 'WRE'.includes(disc)
              ? 'text-start w-40'
              : 'TTE'.includes(disc)
                ? 'text-start w-30'
                : 'HBL'.includes(disc)
                  ? 'text-end'
                  : '|CSL'.includes(disc) ?
                    'text-center'
                    : 'text-end w-30',
        Cell: (props) => {
          return (
            <>
              {props?.cell?.row?.original?.result}
            </>
          )
        }
      },
      {
        Header: '',
        className: 'text-start',
        headerClassName: 'text-start w-5',
        id: 'Records',
        Cell: (props) => (
          props.cell.row.original.recordIndicators
            ? props.cell.row.original.recordIndicators.filter(row => row.ri_order === 1).map((val) => {
              return (<>
                {(val.ri_equalled === 'Y' ? '=' : '') + val.ri_recordtype}
              </>)
            })
            : ''
        )
      }
      ])
  }

  const tableColumns = useMemo(() => {
    let arrTypeDiscipline = ['FBB', 'FBL', 'VBV', 'GTR', 'BMF', 'DIV']
    const expandIrmDisc = ['TTE'] //show expander for everything even IRM
    const expandIrmEvent = /CLB.BLCOMB------------/.test(mirs_event) //show expander for everything even IRM
    const noExpandIrmEvent = /CLB.SPEED-------------/.test(mirs_event) //dont show expander
    let type; //= arrTypeDiscipline.includes(mirs_discipline) ? 'alwaysTrue3' : 'eventRanking'
    let twoColumns = ['BDM', 'TTE']
    let name2Columns = twoColumns?.includes(mirs_discipline) && mirs_event.includes('DOUBLES') //event ranking name displayed in 2 rows
      ? true
      : mirs_discipline === 'BDM' && mirs_event.includes('SINGLES')
        ? false
        : ''

    if (expandIrmDisc.includes(mirs_discipline) || expandIrmEvent) {
      type = ""
    } else if (noExpandIrmEvent) {
      type = 'neverIrm'
    } else {
      type = arrTypeDiscipline.includes(mirs_discipline) ? 'alwaysTrue3' : 'eventRanking'
    }
    console.log('type', type)
    return (
      [
        ColumnRankNoResult({ disableSort: true, isEventRanking: true }),
        ColumnNoc({ disableSort: true }),
        ColumnName({
          disableSort: true,
          useTeamAthletes: name2Columns,
          className: name2Columns ? 'text-start team-duet-container' : 'text-start'
        }),
        ColumnTeamAthletes({}),
        ColumnIrmRanking({ disableSort: true }),
        ColumnExpander({ type: type, disc: mirs_discipline })
      ]
    )

  }, [mirs_discipline, mirs_event]
  )

  // Create a function that will render our row sub components
  function RenderPhases({ event_code, team_code, athlete_code, type }) {
    const code = type === 'A' ? athlete_code : team_code;
    let street = ''
    if (event_code.includes('STREET')) {
      street = true
    }
    let rounds = [];
    const hideTableDbq = code && '|GRY|'.includes(mirs_discipline) && data?.event?.rankings?.find(rk => rk?.athlete_code === code || rk?.team_code === code)?.rk_IRM === 'DQB'
    if ('|MPN|'.includes(mirs_discipline)) {
      let rankingResult = data?.event?.rankings?.find(a => a.athlete_code === code)
      let hasFnlResults = rankingResult?.rk_Result
      code && data?.event?.phases
        ?.filter(phase_ => phase_?.type === '3' && ((phase_?.order === '1' && hasFnlResults) || phase_?.order === '2'))
        ?.sort((a, b) => Number(a.order) - Number(b.order))
        ?.forEach(phase => {
          let textPhase = phase?.shortDescription
          let result
          result = <ResultMpnRace rankingResult={rankingResult} order={phase?.order} />
          rounds.push({
            phase: textPhase,
            phaseOrder: Number(phase?.order),
            teamRank: phase?.order === '1'
              ? rankingResult?.rank
              : findArray(rankingResult?.extendedResults?.find(a => a.extensions)?.extensions, { ere_code: ['RANK'] })?.ere_value,
            result: result
          })
        })
    } else if ('|BMX|'.includes(mirs_discipline)) {
      let eventPhases = data?.event?.phases
      code && eventPhases
        ?.filter(phase_ => phase_?.type === '3')
        ?.sort((a, b) => Number(a.order) - Number(b.order))
        ?.forEach(phase => {
          let textPhase = phase?.shortDescription
          let points, result
          points = <PointsBmx phase={phase} order={phase?.order} code={code} />
          result = <TimeBmx phase={phase} order={phase?.order} code={code} />
          let rank = ''
          if (phase?.order === '1' || phase?.order === '3') {
            rank = phase?.units[0]?.result?.items?.find(ath => ath?.athleteCode === code)?.resultRank
          } else if (phase?.order === '2' || phase?.order === '4') {
            rank = phase?.phaseResult?.items?.find(ath => ath?.athleteCode === code)?.resultRank
          }
          if (rank === undefined)
            return

          rounds.push({
            phase: textPhase,
            phaseOrder: Number(phase?.order),
            teamRank: rank,
            points: points,
            result: result
          })
        })
    } else if ('|GRY|'.includes(mirs_discipline) || /CLB.BLCOMB-.+/.test(mirs_event)) {
      code && data?.event?.phases
        ?.filter(phase_ => phase_?.type === '3')
        ?.sort((a, b) => Number(a.order) - Number(b.order))
        ?.forEach(phase => {
          let rival = phase?.cumulative?.results?.find(res => res?.participant?.code !== code)
          return phase?.cumulative?.results?.filter(res => res?.participant?.code === code)?.forEach(item => {
            let textPhase = phase?.shortDescription
            let result
            if (mirs_discipline === 'GRY') {
              result = <ResultDefaultRace unit={phase?.cumulative} code={code} isCumulativeSource={true} />
            } else {
              result = <ResultDefaultRace unit={phase?.cumulative} code={code} isCumulativeSource={true} />
            }
            rounds.push({
              phase: textPhase,
              phaseOrder: Number(phase?.order),
              eventUnitCode: phase?.cumulative?.documentCode,
              dateMatch: '',
              teamRes: item?.resultData,
              teamWLT: item?.resultWLT,
              teamPos: item?.sortOrder,
              teamRank: item?.resultRank,
              //Team athletes from DT_RANKING
              teamAthletes: findArray(data?.event?.rankings, { team_code: [item?.participant?.code] }).teamAthletes,
              //Team athletes from DT_RESULT
              teamAthletesResult: item?.teamAthletes,
              run1: '',
              run2: '',
              rival: rival?.participant,
              rivalNoc: rival?.participant?.organisation,
              rivalRes: rival?.resultData,
              rivalCode: `${rival?.participant?.code}_${phase?.cumulative?.documentCode}`,
              competitorPlace: rival?.teamCode ? rival?.teamCode : rival?.athleteCode,
              result: result
            })
          })


        })

    } else {
      const cumulativeDataforEQU = data?.event?.phases?.find(f => f?.cumulative)?.cumulative;

      const phases = data?.event?.phases?.filter(p => p?.type === "3")?.sort((a, b) => Number(a.order) - Number(b.order))

      code && Array.isArray(phases) && phases?.forEach(phase => {

        const units = phase?.units?.filter(u => u?.result)?.sort((a, b) => {

          let timeStampA = Date.parse(a?.result?.schedule?.startDate)
          let timeStampB = Date.parse(b?.result?.schedule?.startDate)

          if (isNaN(timeStampA) || isNaN(timeStampB)) {
            timeStampA = Date.parse(a?.result?.date)
            timeStampB = Date.parse(b?.result?.date)
          }

          return timeStampB - timeStampA
        });

        Array.isArray(units) && units?.forEach(unit => {

          let rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

          Array.isArray(unit?.result?.items) && unit?.result?.items?.forEach(item => {

            let textPhase = phase?.shortDescription
            if (unitDescriptionForFinal?.includes(mirs_discipline) && phase?.order?.trim() === '1') {
              textPhase = `${textPhase} - ${unit?.shortDescription}`
            }
            if (unitDescription?.includes(mirs_discipline)) {
              textPhase = unit?.shortDescription
              if (/EQU.DRES.+/.test(mirs_event)) { //specific case for EQU dressage
                textPhase = phase?.shortDescription === "Grand Prix - Qualifier" ? phase?.units?.find(e => e.type === "NONE")?.shortDescription
                  : unit?.shortDescription
              }
            }
            if (item?.participant?.code === code) {
              let result;
              if (mirs_discipline === 'ARC') {
                result = <ResultArc unit={unit} code={code} />
              } else if (mirs_discipline === 'BDM') {
                result = <ResultBdm unit={unit} code={code} />
              } else if (mirs_discipline === 'BMF') {
                result = <ResultBmf unit={unit} code={code} />
              } else if (mirs_discipline === 'DIV') {
                result = <ResultDiv unit={unit} code={code} />
              } else if (mirs_discipline === 'EQU') {
                result = <ResultEqu data={unit} cumulativeData={cumulativeDataforEQU} code={code} />
              } else if (mirs_discipline === 'FBL' || mirs_discipline === 'FBB') {
                result = <ResultDefaultH2hIrmPSO unit={unit} code={code} />
              } else if (mirs_discipline === 'GBL') {
                result = <ResultDefaultH2hIrmPSO unit={unit} code={code} />
              } else if (['FEN', 'WFE'].includes(mirs_discipline)) {
                result = <ResultFen unit={unit} code={code} />
              } else if (mirs_discipline === 'HOC') {
                result = <ResultHoc unit={unit} code={code} />
              } else if (mirs_discipline === 'JUD') {
                result = <ResultJud unit={unit} code={code} />
              } else if (mirs_discipline === 'SRF') {
                result = <ResultSrf unit={unit} code={code} />
              } else if (mirs_discipline === 'PDL') {
                result = <ResultPdl unit={unit} code={code} />
              } else if (mirs_discipline === 'BKG') {
                result = <ResultBkg unit={unit} code={code} />
              } else if (['BOX', 'KIK', 'MTI'].includes(mirs_discipline)) {
                result = <ResultDefaultCombat unit={unit} code={code} />
              } else if (mirs_discipline === 'WRE') {
                result = <ResultWre unit={unit} code={code} />
              } else if (mirs_discipline === 'WPO') {
                result = <ResultWpo unit={unit} code={code} />
              } else if (mirs_discipline === 'SWM') {
                result = <ResultSwm unit={unit} code={code} />
              } else if (mirs_discipline === 'SKB') {
                result = <ResultSkb unit={unit} code={code} />
              } else if (['VBV', 'VVO', 'VBS', 'KTE'].includes(mirs_discipline)) {
                result = <ResultDefaultH2hIrm unit={unit} code={code} />
              } else if (['CLB', 'CSL', 'CSP', 'GRY', 'ROW', 'SHO', 'GTR', 'EQU'].includes(mirs_discipline)) {
                result = <ResultDefaultRace unit={unit} code={code} />
              } else if (mirs_discipline === 'TTE') {
                result = <ResultDefaultH2hIrmTTE unit={unit} code={code} />
              } else {
                result = <ResultDefaultH2hIrm unit={unit} code={code} />
              }

              rounds.push({
                unit: unit?.shortDescription,
                phase: textPhase,
                phaseOrder: Number(phase?.order),
                eventUnitCode: unit?.result?.eventUnitCode,
                dateMatch: unit?.result?.date,



                teamRes: item?.resultData,
                teamWLT: item?.resultWLT,
                teamPos: item?.sortOrder,
                teamRank: item?.resultRank,
                //Team athletes from DT_RANKING
                teamAthletes: findArray(data?.event?.rankings, { team_code: [item?.participant?.code] }).teamAthletes,
                //Team athletes from DT_RESULT
                teamAthletesResult: item?.teamAthletes,

                run1: findArray(item?.extendedResults, { er_pos: ['RUN1'] })?.er_irm
                  ? findArray(item?.extendedResults, { er_pos: ['RUN1'] })?.er_irm
                  : findArray(item?.extendedResults, { er_pos: ['RUN1'] })?.er_value,

                run2: findArray(item?.extendedResults, { er_pos: ['RUN2'] })?.er_irm
                  ? findArray(item?.extendedResults, { er_pos: ['RUN2'] })?.er_irm
                  : findArray(item?.extendedResults, { er_pos: ['RUN2'] })?.er_value,

                recordIndicators: item?.recordIndicators,

                rival: rival?.participant,
                rivalNoc: rival?.participant?.organisation,
                rivalRes: rival?.resultData,
                rivalCode: `${rival?.participant?.code}_${unit?.result?.eventUnitCode}`,
                competitorPlace: rival?.teamCode ? rival?.teamCode : rival?.athleteCode,
                result: result,
                participantCode: code
              })
            }

          })
        }
        )
      })

    }

    let hiddenColumns = []
    switch (mirs_discipline) {
      case 'ARC':
        hiddenColumns = ['Pos', 'Rank', 'Run1', 'Run2', 'Noc', 'WLTRank', 'Points', 'Records']
        break;
      case 'BMF':
        hiddenColumns = ['Pos', 'NocArc', 'Rank', 'Noc', 'WLTRank', 'Points', 'Records']
        break;
      case 'BMX':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLT', 'WLTRank', 'Records']
        break;
      case 'CLB':
        hiddenColumns = ['Pos', 'Rank', 'Run1', 'Run2', 'NocArc', 'WLT', 'Points', 'Records']
        break;
      case 'CSL':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank', 'Points', 'Records']
        break;
      case 'CSP':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank', 'Points', 'Records']
        break;
      case 'CTR':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank', 'Points', 'Result', 'Records']
        break;
      case 'DIV':
        hiddenColumns = ['Pos', 'Noc', 'Run1', 'Run2', 'NocArc', 'WLTRank', 'Points', 'Records', 'WLT']
        break;
      case 'EQU':
        hiddenColumns = ['Pos', 'Noc', 'Run1', 'Run2', 'NocArc', 'WLTRank', 'WLT', 'Points', 'Records']
        break;
      case 'GRY':
        hiddenColumns = ['Pos', 'Noc', 'Run1', 'Run2', 'NocArc', 'WLTRank', 'Points', 'Records']
        break;
      case 'GTR':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank', 'Points', 'Records']
        break;
      case 'MPN':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank', 'Points', 'Records']
        break;
      case 'ROW':
        hiddenColumns = ['Pos', 'Noc', 'Run1', 'Run2', 'NocArc', 'WLTRank', 'Points', 'Records']
        break;
      case 'SHO':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank', 'Points']
        break;
      case 'SRF':
        hiddenColumns = ['Noc', 'WLT', 'Rank', 'Run1', 'Run2', 'NocArc', 'WLTRank', 'Points', 'Records']
        break;
      case 'SWM':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank', 'Points', 'Records']
        break;
      case 'SKB':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLT', 'WLTRank', 'Points', 'Records']
        break;
      default:
        hiddenColumns = ['Pos', 'Rank', 'Run1', 'Run2', 'NocArc', 'WLTRank', 'Points', 'Records']
        break;
    }

    let hasRecords = false
    data?.event?.phases.filter(p => p.type === '3').forEach(phase => {
      phase?.units?.forEach(u => {
        if (u?.result?.items?.some(i => i.recordIndicators !== undefined)) {
          hasRecords = true
        }
      })
    })
    if (!hasRecords) hiddenColumns.push('Records')

    if (/CLB.BLCOMB-.+/.test(mirs_event)) {
      hiddenColumns = ['Pos', 'Run1', 'Run2', 'NocArc', 'Noc', 'WLT', 'WLTRank', 'Points', 'Records']
    }

    const positions = usePositions();
    const athletesSorts = useTeamAthletesOrderByPositions(rounds[0]?.teamAthletesResult, true);

    const cumulativeDataforEQU = /EQU.EVE.+/.test(mirs_event) ? data?.event?.phases?.find(f => f?.cumulative)?.cumulative : undefined;

    return (
      <>
        {mirs_discipline === 'SWM' && mirs_event.includes('SWMX')
          ? <div className='competitor-box'>{rounds[0]?.teamAthletes?.map((ath, index) =>
            <div className='competitor-gender' key={index}>
              <Competitor {...ath.participant} hidePicture={true} hideNoc={true} />
              &nbsp;{ath?.participant?.personGender?.code}
            </div>
          )}</div>
          : <></>
        }

        {mirs_discipline === 'ROW' && type !== 'A'
          ? <div className='competitor-box'>
            <table className='table table-sm table-borderless w-40'>
              <tbody>
                {athletesSorts?.map(ath => (
                  <tr key={ath?.athlete?.code}>
                    <td className='w-30'><Competitor {...ath.athlete} hidePicture={true} hideNoc={true} /></td>
                    <td className='w-5'>{positions(ath?.eventUnitEntries.find(item => item.code === 'POSITION')?.eue_value)}</td>
                    {(getRscObject(mirs_event)?.sex === 'X' || getRscObject(mirs_event)?.event === 'COXED8------------') && <td className='w-5'>{ath?.athlete?.personGender?.code}</td>}
                  </tr>
                )
                )}
              </tbody>
            </table>
          </div>
          : <></>
        }

        {mirs_discipline === 'CSP' && type !== 'A'
          ? <div className='competitor-box'>{rounds[0]?.teamAthletesResult?.map((ath, index) =>
            <div className='competitor-list' key={index}>
              <div className='text-center w-20'>
                <Competitor {...ath.athlete} hidePicture={true} hideNoc={true} />
              </div>
            </div>
          )}</div>
          : <></>
        }
        {/GRY.5AA-.+/.test(mirs_event)
          ? <div className='competitor-box'>{rounds[0]?.teamAthletesResult?.map(ath =>
            <div className='competitor-list' key={ath?.participantCode?.code}>
              <div className='text-center w-20'>
                <Competitor {...ath.participantCode} hidePicture={true} hideNoc={true} />
              </div>
            </div>
          )}</div>
          : <></>
        }

        <MirsTable
          className={hideTableDbq ? 'd-none' : 'table-detail'}
          data={rounds}
          columns={useMemo(() => tableColumnsDetail(mirs_discipline, street, cumulativeDataforEQU), [street, cumulativeDataforEQU])}
          hiddenColumns={hiddenColumns}
          keyRow={"rivalCode"}
          //initialSortBy={[{ id: 'Phase', desc: false }]}
          id={`mirs-table-event-ranking-detail-${mirs_event}`}
        />
      </>
    )
  }
  const renderRowSubComponent = React.useCallback(({ row }) => (
    <LoaderSubComponent component={<RenderPhases
      event_code={row?.original?.event_code}
      team_code={row?.original?.team_code}
      athlete_code={row?.original?.athlete_code}
      type={row?.original?.rk_Type}
    />}
    />
  ), []);



  let keyRow = "team_code"
  if (data?.event?.rankings !== undefined && data?.event?.rankings[0] !== undefined) {
    keyRow = data?.event?.rankings[0]?.rk_Type === 'A' ? "athlete_code" : keyRow
  }

  let hiddenColumns = [];
  if (mirs_discipline !== 'BDM') {
    hiddenColumns.push('Irm')
  }
  if (/EQU.EVE.+/.test(mirs_event)) {
    hiddenColumns.push('Expander')
  }
  if (mirs_discipline === 'SAL') {
    hiddenColumns.push('Expander')
  }

  if (showTeamAthletesInMainTable(mirs_event)) {
    hiddenColumns.push('Name')
  } else {
    hiddenColumns.push('TeamAthletes')
  }

  return (
    <>
      <LegendProvider>
        <CleanLegends />
        <div className='mirs-page-container'>
          <MirsTable
            className='table-result'
            data={data?.event?.rankings}
            hiddenColumns={hiddenColumns}
            columns={tableColumns}
            keyRow={keyRow}
            colIndex={1}
            caption={<Label data='LabelEventRanking' />}
            initialSortBy={[{ id: 'RankNoResult', desc: false }]}
            id={`mirs-table-event-ranking-${mirs_event}`}
            renderRowSubComponent={renderRowSubComponent}
          />
        </div>
        <LegendsBox />
      </LegendProvider>
    </>

  )
}

export default EventRanking

// ### DEFAULTS ###
/*
const ResultDefaultH2H = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  return (
    <Stack direction='horizontal' className='justify-content-center'>
      <div className='w-40'>{main?.resultData}</div>
      <div className='text-center'>{rival?.resultData && ' - '}</div>
      <div className='w-40 text-start'>{rival?.resultData}</div>
    </Stack>
  )
}
*/


const ResultDefaultH2hIrm = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const { breakPoint } = useViewport();
  const isMobile = breakPoint === "sm" || breakPoint === "xs"

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />
  //  style={{ marginRight: (mirs_discipline === 'VVO' || mirs_discipline === 'VBS') && !isMobile ? '100px' : '' }}
  return (
    <Stack direction='horizontal' className='justify-content-center ' style={{ marginLeft: (mirs_discipline === 'VVO' || mirs_discipline === 'VBS') ? '15px' : '' }}>
      <div className='w-40'>{irmMain} {main?.resultData}</div>
      <div className='text-center'>{(rival?.resultData || (irmMain && irmRival)) && ' - '}</div>
      <div className='w-40 text-start d-flex'>{rival?.resultData} {irmRival && <>&nbsp;{irmRival}</>}</div>
    </Stack>
  )
}

const ResultDefaultH2hIrmPSO = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const { breakPoint } = useViewport();
  const isMobile = breakPoint === "sm" || breakPoint === "xs"


  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  const pso = unit?.result?.periods?.find(it => it?.period_code.includes('-PSO'))
  const mainPSO = main?.startSortOrder === 1 ? pso?.home?.periodScore : pso?.away?.periodScore
  const rivalPSO = main?.startSortOrder === 1 ? pso?.away?.periodScore : pso?.home?.periodScore

  const resultPSO = pso && <>{'(' + mainPSO + '-' + rivalPSO + ' '}<SportCode discipline={mirs_discipline} entity='@Period' code='PSO' abbr={true} />{')'}</>

  return (
    <>
      <Stack direction='horizontal' className='justify-content-center mx-0'>
        <div className='w-40'>{irmMain} {main?.resultData}</div>
        <div className='text-center'>{(rival?.resultData || (irmMain && irmRival)) && ' - '}</div>
        <div className='w-40 text-start'>{rival?.resultData} {irmRival}</div>
        <div className='w-80 text-start'> {!isMobile && resultPSO}</div>
      </Stack>
      {isMobile &&
        <div className='text-center d-block'>
          {resultPSO}
        </div>
      }
    </>
  )
}

const ResultDefaultH2hIrmTTE = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  return (
    <Stack direction='horizontal' className='justify-content-center'>
      <div className='w-40 text-end'>{irmMain} {main?.resultData}</div>
      <div className='text-center'>{(rival?.resultData || (irmMain && irmRival)) && ' - '}</div>
      <div className='w-40 text-start'>{rival?.resultData} {irmRival}</div>
    </Stack>
  )
}

const ResultDefaultRace = ({ unit, code, isCumulativeSource }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = isCumulativeSource
    ? unit?.results?.find(it => it?.participant?.code === code)
    : unit?.result?.items?.find(it => it?.participant?.code === code);

  const mainIrm = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />

  return (
    <>
      {main?.resultData} {mainIrm}
    </>
  )
}

const ResultDefaultCombat = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  let resCode = unit?.result?.extendedInfos?.find(ei => ei?.ei_code === 'RES_CODE')?.ei_value
  let round = unit?.result?.extendedInfos?.find(ei => ei?.ei_code === 'ROUND')?.ei_value
  let resultType = main?.resultType

  let win = false
  if (main?.resultWLT?.trim() === 'W') {
    win = true
  }

  return (
    <>
      {win && resultType === 'POINTS' && main?.resultData && rival?.resultData
        ? <>{irmMain} {<SportCode discipline={mirs_discipline} entity='@ResultCode' code={resCode} abbr={true} />} {main?.resultData}:{rival?.resultData} {irmRival}</>
        : win && (resultType === 'RM' || resultType === 'RM_POINTS')
          ? <>{irmMain} {mirs_discipline === 'BOX' ? <><SportCode discipline={mirs_discipline} entity='@ResultCode' code={resCode} abbr={true} /> {round}</> : main?.resultType}</>
          : !win && resultType === 'POINTS' && main?.resultData && rival?.resultData
            ? <>{irmMain} {<SportCode discipline={mirs_discipline} entity='@ResultCode' code={resCode} abbr={true} />} {main?.resultData}:{rival?.resultData} {irmRival}</>
            : !win && (resultType === 'RM' || resultType === 'RM_POINTS')
              ? <>{irmRival} {mirs_discipline === 'BOX' ? <><SportCode discipline={mirs_discipline} entity='@ResultCode' code={resCode} abbr={true} /> {round}</> : rival?.resultType}</>
              : <></>
      }
    </>
  )
}


//  ### ARC ###
const ResultArc = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  const mainQualRank = main?.resultRank && `(${main?.resultRank})`

  const hyphen = ((rival?.resultData || irmRival) || (irmMain && irmRival)) && '-'

  let result;
  if (unit?.result?.eventUnitCode.substring(22, 26) === 'QUAL') {
    result = <>{irmMain} {main?.resultData} {mainQualRank}</>
  } else {
    result = <>{irmMain} {main?.resultData} {hyphen} {rival?.resultData} {irmRival}</>
  }
  return (
    <>
      {result}
    </>
  )
}

//  ### BDM ###
const ResultBdm = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  let games = unit?.result?.periods?.map(item => item?.home?.periodScore + '-' + item?.away?.periodScore).join(' ')

  games = games && ('(' + games + ')')

  const hyphen = ((rival?.resultData || irmRival) || (irmMain && irmRival)) && '-'

  return (
    <>
      <div className='d-flex'>
        <div className='w-40 text-end'>{irmMain} {main?.resultData} {hyphen} {rival?.resultData} {irmRival}</div>
        <div className='ps-2 w-60 text-start event-ranking-games align-self-center'>{games}</div>
      </div>
    </>
  )
}

//  ### BKG ###
const ResultBkg = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  const mainVotes = main?.extendedResults?.find(filter => filter.er_code === 'VOTES')?.er_value
  const rivalVotes = rival?.extendedResults?.find(filter => filter.er_code === 'VOTES')?.er_value

  const votes = mainVotes && rivalVotes && `(${mainVotes}-${rivalVotes})`;

  const hyphen = ((rival?.resultData || irmRival) || (irmMain && irmRival)) && '-'

  return (
    <>
      {irmMain} {main?.resultData} {hyphen} {rival?.resultData} {votes} {irmRival}
    </>
  )
}

//  ### BMF ###
const ResultBmf = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />

  return (
    <>
      {irmMain} {main?.resultData}
    </>
  )
}

//  ### BMX ###
const PointsBmx = ({ phase, order, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = phase?.phaseResult?.items?.find(ath => ath?.athleteCode === code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />

  const points = order === '2' || order === '4' ? main?.resultData : '-'

  return (
    <>
      {irmMain} {points}
    </>
  )
}

const TimeBmx = ({ phase, order, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = Array.isArray(phase?.units) && phase?.units?.length > 0 ? phase?.units[0]?.result?.items?.find(ath => ath?.athleteCode === code) : {}

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />

  const result = order === '1' || order === '3' ? main?.resultData : '-'

  return (
    <>
      {irmMain} {result}
    </>
  )
}

//  ### DIV ###
const ResultDiv = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />

  return (
    <>
      {irmMain} {main?.resultData}
    </>
  )
}

//  ### EQU ###
const ResultEqu = ({ data, cumulativeData, code }) => {
  const { mirs_event } = useUrlFriendlyToParameters()

  const main = data?.result?.items?.find(it => it?.participant?.code === code);
  //const penalties = main?.extendedResults?.find(er => er?.er_code?.includes('TOTAL_PEN'))?.er_value
  const score = main?.resultData;

  const cumulativeResult = cumulativeData?.results?.find(it => it?.participant?.code === code)
  const cumulativeResultItem = cumulativeResult?.items?.find(i => i?.eventUnitCode === data?.result.eventUnitCode);
  const erAfter = cumulativeResultItem?.extendedResults?.find(er => er?.er_code === 'TOTAL_PEN')
  //const cumulativePenalties = erAfter?.er_value;

  return (
    <>
      {/EQU.DRES.+/.test(mirs_event) && score /* : /EQU.EVE.+/.test(mirs_event) ? cumulativePenalties : penalties */}
    </>
  )
}

const RankCumulativeEqu = ({ row, cumulativeData }) => {
  const cumulativeResult = cumulativeData?.results?.find(it => it?.participant?.code === row?.participantCode)
  const cumulativeResultItem = cumulativeResult?.items?.find(i => i?.eventUnitCode === row?.eventUnitCode);
  const erAfter = cumulativeResultItem?.extendedResults?.find(er => er?.er_code === 'AFTER')
  const rank = erAfter?.er_rank && erAfter?.er_rankequal ? `=${erAfter?.er_rank}` : erAfter?.er_rank;
  return (
    <>
      {rank}
    </>
  )
}

//  ### FEN ###
const ResultFen = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const winnerId = unit?.result?.extendedInfos?.find(ei => ei?.ei_code === 'WINNER_ID')?.ei_value
  const toss = unit?.result?.extendedInfos?.find(ei => ei?.ei_code === 'TOSS')?.ei_value

  const tossMain = toss && winnerId === main?.participant?.code && 'V(T)'
  const tossRival = toss && winnerId === rival?.participant?.code && 'V(T)'

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  const hyphen = ((rival?.resultData || irmRival) || (irmMain && irmRival)) && '-'

  const result = <>{tossMain} {irmMain} {main?.resultData} {hyphen} {rival?.resultData} {irmRival} {tossRival}</>

  return (
    <>
      {result}
    </>
  )
}

//  ### HOC ###
const ResultHoc = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const pso = unit?.result?.periods?.find(p => p.period_code === `${unit?.result?.eventUnitCode}PSO`)
  const isMainHome = unit?.result?.items?.find(i => i.startSortOrder === 1)?.participant?.code === code

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  const resultPso = pso?.home?.periodScore && pso?.away?.periodScore && (isMainHome ? `(${pso?.home?.periodScore} - ${pso?.away?.periodScore})` : `(${pso?.away?.periodScore} - ${pso?.home?.periodScore})`)

  const hyphen = ((rival?.resultData || irmRival) || (irmMain && irmRival)) && '-'

  const result = <>{irmMain} {main?.resultData} {hyphen} {rival?.resultData} {resultPso} {irmRival}</>

  return (
    <>
      {result}
    </>
  )
}

//  ### JUD ###
const ResultJud = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  let teamS = parseInt(main?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'S'))?.er_value)
  let teamH = main?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'H'))?.er_value
  let teamX = main?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'X'))?.er_value
  let teamS3 = main?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'S3'))?.er_value

  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  let rivalS = parseInt(rival?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'S'))?.er_value)
  let rivalH = rival?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'H'))?.er_value
  let rivalX = rival?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'x'))?.er_value
  let rivalS3 = rival?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'S3'))?.er_value

  let teamPenalties;
  let rivalPenalties;
  if (teamS > 0) {
    for (let i = 0; i < teamS; i++) {
      teamPenalties = <>{teamPenalties} <SvgMirs className='mirsIcon' title='LabelYCDesc' code='yellow-card' />
      </>
    }
  } else if (teamH === '1' || teamX === '1' || teamS3 === '1') {
    teamPenalties = <SvgMirs className='mirsIcon' title='HeaderRCDesc' code='red-card' />
  }
  if (rivalS > 0) {
    for (let i = 0; i < rivalS; i++) {
      rivalPenalties = <>{rivalPenalties}
        <SvgMirs className='mirsIcon' title='LabelYCDesc' code='yellow-card' />
      </>
    }
  } else if (rivalH === '1' || rivalX === '1' || rivalS3 === '1') {
    rivalPenalties = <SvgMirs className='mirsIcon' title='LabelRCDesc' code='red-card' />
  }

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  const hyphen = ((rival?.resultData || irmRival) || (irmMain && irmRival)) && '-'

  let result = (main?.resultData) ?
    (<div className='d-flex w-100 justify-content-center'>
      <div className='w-50'>{irmMain} {main?.resultData}{teamPenalties}</div>
      <div>&nbsp;{hyphen}&nbsp;</div>
      <div className='w-50 text-start'>{rival?.resultData}{rivalPenalties} {irmRival}</div>
    </div>)
    :
    (<div className="w-100 text-center">
      <SportCode discipline={mirs_discipline} entity='@CompetitorPlace' code={rival?.teamCode ? rival?.teamCode : rival?.athleteCode} />
    </div>)
  return (
    <>
      {result}
    </>
  )
}

//  ### MPN ###
const ResultMpnRace = ({ rankingResult, order }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  let result = ''
  if (order === '1') {
    result = rankingResult.rk_Result
  } else if (order === '2') {
    result = findArray(rankingResult?.extendedResults, { er_code: ['SEMIFINAL'] }).er_value
  }

  const irmMain = rankingResult?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rankingResult?.resultIrm} abbr={true} />

  return (
    <>
      {irmMain} {result}
    </>
  )
}

//  ### PDL ###
const ResultPdl = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  const scores = unit?.result?.periods?.map(p => p?.home?.score + '-' + p?.away?.score).join(' ')

  return (
    <>{irmMain} {scores} {irmRival}</>
  )
}

//  ### SKB ###
const ResultSkb = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />

  return (
    <>
      {irmMain} {main?.resultData}
    </>
  )
}

//  ### SRF ###
const ResultSrf = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />

  return (
    <>
      {irmMain} {main?.resultData}
    </>
  )
}

//  ### SWM ###
const ResultSwm = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />

  return (
    <>
      {irmMain} {main?.resultData}
    </>
  )
}

//  ### WRE ###
const ResultWre = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const periods = unit?.result?.periods

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  if (Array.isArray(periods) && periods.length > 0) {
    if (periods[1]) {
      main.resultData = getScoreForParticipiant(periods[1], main?.participant?.code)
      rival.resultData = getScoreForParticipiant(periods[1], rival?.participant?.code)
    }
    else {
      main.resultData = getScoreForParticipiant(periods[0], main?.participant?.code)
      rival.resultData = getScoreForParticipiant(periods[0], rival?.participant?.code)
    }

  }

  let winCon = findArray(unit?.result?.extendedInfos, { ei_code: ['RES_CODE'] }).ei_value ? findArray(unit?.result?.extendedInfos, { ei_code: ['RES_CODE'] }).ei_value : ''

  const hyphen = ((rival?.resultData || irmRival) || (irmMain && irmRival)) && '-'

  return (
    <div className='w-30'>
      {irmMain} {main?.resultData} {hyphen} {rival?.resultData} {winCon} {irmRival}
    </div>
  )
}

const ResultWpo = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  let mainText = main?.resultDataText
  let rivalText = rival?.resultDataText
  const mainResult = mainText ? JSON.parse(mainText) : [];
  const rivalResult = rivalText ? JSON.parse(rivalText) : [];

  const irmMain = main?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} />
  const irmRival = rival?.resultIrm && <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} />

  return (
    <Stack direction='horizontal' className='justify-content-center'>
      {main?.resultData && <div>{irmMain} {main?.resultData}-{rival?.resultData} {irmRival}</div>}
      {mainResult?.extra?.psoRanking && <div>&nbsp;({mainResult?.extra?.psoRanking}-{rivalResult?.extra?.psoRanking})</div>}
    </Stack>
  )
}

function getScoreForParticipiant(period, code) {
  return period?.home?.participant?.code === code ? period?.home?.score : period?.away?.score
}

function showTeamAthletesInMainTable(rsc) {
  return (
    /DIV...TEAM2-.+/.test(rsc) ||
    /DIV....TEAM2-.+/.test(rsc)
  )
}